import type { LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  /** Halk darabszám a felirat után, zárójelben (pl. „A szerzőktől (12)"). */
  count?: number;
}

/** Lapfülek egy oldalon belüli tartalomváltáshoz (fejezet-oldal, Ajánló).
 *
 *  Alsó 1 px `line` sáv, az aktív fül 2 px `gold` aláhúzással és `ink` felirattal; a
 *  szegmentált vezérlőtől (`Segmented`) szándékosan más a formája, mert az űrlap-állapotot
 *  választ, ez pedig navigál. A fülek egyenlő szélességűek, hogy ikonnal együtt is kiférjenek
 *  a legkeskenyebb telefonon; ha mégsem, a sáv vízszintesen görgethető.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="-mx-5 flex gap-1 overflow-x-auto border-b border-line px-5 md:mx-0 md:px-0"
    >
      {tabs.map((t) => {
        const active = t.value === value;
        const Icon = t.icon;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              "-mb-px flex min-h-11 flex-1 items-center justify-center gap-1.5 whitespace-nowrap border-b-2 px-2 text-[13px] transition-colors",
              active
                ? "border-gold font-bold text-ink"
                : "border-transparent font-semibold text-muted hover:text-ink-soft",
            )}
          >
            {Icon && <Icon size={16} strokeWidth={1.8} className={active ? "text-gold" : ""} />}
            {t.label}
            {t.count !== undefined && (
              <span className="text-[12px] font-medium text-muted">({t.count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
